/**
 * Ownership-share formatting, shared by every surface that renders a
 * `beneficial_ownership` value.
 *
 * Lives in utils because two features use it: the Key Contacts card and the shareholder
 * structure views. Same reason `isNavigableCrefoId` sits in utils/idUtils.
 *
 * The number reaches two surfaces one tab apart (the ownership tables and a key contact's
 * "Is also a shareholder" cell), so it has exactly one implementation: a change to how `≥`, `GP`
 * or fractional values display cannot land in one and miss the other.
 */

// The operators of a bounded share, as the graph filter reads them (graphFilters.js).
const BOUND_PREFIXES = ['>', '≥', '<'];

/**
 * Formats a raw `beneficial_ownership` value for display.
 *
 * Returns `null` when there is no share to show, leaving the caller to choose its own fallback:
 * the ownership tables render the shared "n/a" placeholder, while a contact card falls through
 * to the plain "Yes" it renders today.
 *
 * @param {string|number|null|undefined} rawValue - as served on a shareholder node. Real values
 *   include `"25"`, `"33.33"`, `">50"`, `"≥25"`, `"<5"`, `"GP"` (a general partner holding no
 *   equity) and `null`.
 * @returns {string|null} e.g. `"25%"`, `">50%"`, `"<5%"`, `"GP"`, or `null` when there is nothing
 *   to show.
 */
export const formatOwnershipShare = (rawValue) => {
  if (rawValue == null) {
    return null;
  }
  if (typeof rawValue === 'number') {
    return `${rawValue}%`;
  }
  // String() rather than a bare .trim(): the value is served from two different queries and an
  // unexpected type should degrade to "no share", not throw inside a render.
  const share = String(rawValue).trim();
  if (!share) {
    return null;
  }
  // ">50" / "≥25" / "<5" keep their operator and gain the percent sign. "GP" and any other
  // non-numeric text passes through verbatim rather than being dressed up as a percentage.
  const hasPrefix = BOUND_PREFIXES.some((prefix) => share.startsWith(prefix));
  const numericPart = hasPrefix ? share.slice(1).trim() : share;
  // Number() rejects mixed strings like "25GP" (NaN), unlike parseFloat which would extract 25.
  const isNumeric = numericPart !== '' && !Number.isNaN(Number(numericPart));
  return isNumeric ? `${share}%` : share;
};
